import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateBoard } from '../../hooks/useBoards';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import './CreateBoardModal.css';

export interface CreateBoardModalProps {
  workspaceId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * CreateBoardModal Component
 * Modal for creating a new board
 * Implements Requirement 4.1: Board creation succeeds for workspace members
 */
export const CreateBoardModal: React.FC<CreateBoardModalProps> = ({
  workspaceId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});

  const createBoard = useCreateBoard(workspaceId);

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = t('board.boardNameRequired');
    } else if (name.trim().length > 100) {
      newErrors.name = t('board.boardNameTooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await createBoard.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined
      });

      // Reset form
      setName('');
      setDescription('');
      setErrors({});

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleClose = () => {
    if (!createBoard.isPending) {
      setName('');
      setDescription('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('board.createBoard')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="create-board-modal__form">
        <Input
          label={t('board.name')}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          placeholder={t('board.enterName')}
          required
          autoFocus
        />

        <Input
          label={t('board.descriptionOptional')}
          type="textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('board.enterDescription')}
          rows={3}
        />

        <div className="create-board-modal__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createBoard.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={createBoard.isPending}
          >
            {t('board.createBoard')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
